import React, { useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, Row } from 'react-bootstrap'

import ProviderService from '../../../services/ProviderService'

export default function MalformedNameCard({ item, onResolvedChange }) {
  const [title, setTitle] = useState(item.suggestedTitle || '')
  const [firstName, setFirstName] = useState(item.suggestedFirstName || '')
  const [middleName, setMiddleName] = useState(item.suggestedMiddleName || '')
  const [lastName, setLastName] = useState(item.suggestedLastName || '')
  const [suffix, setSuffix] = useState(item.suggestedSuffix || '')

  const [isApplying, setIsApplying] = useState(false)
  const [isUndoing, setIsUndoing] = useState(false)
  const [error, setError] = useState(null)
  const [resolvedState, setResolvedState] = useState(null)

  const handleApply = async () => {
    setIsApplying(true)
    setError(null)
    try {
      const response = await ProviderService.fixMalformedName({
        providerId: item.providerId,
        title: title.trim() || null,
        firstName: firstName.trim() || null,
        middleName: middleName.trim() || null,
        lastName: lastName.trim() || null,
        suffix: suffix.trim() || null,
      })

      if (response && response.success) {
        setResolvedState({
          actionType: 'fix-name',
          undoActionId: response.undoActionId,
          undoPayloadJson: response.undoPayloadJson || response.undoActionId,
          message: response.message || 'Name format successfully updated.',
        })
        if (onResolvedChange) onResolvedChange(item.providerId, true)
      } else {
        setError(response?.message || 'Failed to update name format.')
      }
    } catch (err) {
      setError(err?.message || 'Error occurred while updating name.')
    } finally {
      setIsApplying(false)
    }
  }

  const handleUndo = async () => {
    if (!resolvedState?.undoPayloadJson && !resolvedState?.undoActionId) return
    setIsUndoing(true)
    setError(null)
    try {
      await ProviderService.undoLibraryAction({
        actionType: resolvedState.actionType,
        undoActionId: resolvedState.undoActionId,
        previousStateJson: resolvedState.undoPayloadJson,
      })
      setResolvedState(null)
      if (onResolvedChange) onResolvedChange(item.providerId, false)
    } catch (err) {
      setError(err?.message || 'Failed to undo name correction.')
    } finally {
      setIsUndoing(false)
    }
  }

  const handleSkip = () => {
    setResolvedState({
      actionType: 'dismiss',
      undoPayloadJson: null,
      message: 'Skipped name formatting.',
    })
    if (onResolvedChange) onResolvedChange(item.providerId, true)
  }

  return (
    <Card className={`mb-3 border-${resolvedState ? 'success' : 'light'} shadow-sm`}>
      <Card.Header className="d-flex justify-content-between align-items-center bg-white py-2">
        <div className="d-flex align-items-center">
          <span className="mr-2" role="img" aria-label="malformed">
            ✏️
          </span>
          <strong className="text-dark">Malformed Name Entry</strong>
        </div>
        {resolvedState && (
          <Badge variant="success" className="p-1 px-2">
            ✓ Resolved
          </Badge>
        )}
      </Card.Header>
      <Card.Body className="py-2">
        {error && (
          <Alert variant="danger" className="py-1 small mb-2">
            {error}
          </Alert>
        )}

        {resolvedState ? (
          <div className="d-flex justify-content-between align-items-center py-2">
            <span className="text-success font-weight-bold">✓ {resolvedState.message}</span>
            {resolvedState.undoPayloadJson && (
              <Button variant="outline-secondary" size="sm" onClick={handleUndo} disabled={isUndoing}>
                {isUndoing ? 'Undoing...' : 'Undo'}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="mb-2">
              <span className="text-muted small">{item.issueDescription}</span>
            </div>

            <div className="bg-light p-2 rounded mb-2 border small">
              <Row className="text-muted mb-1 font-weight-bold">
                <Col md={6}>Current Values</Col>
                <Col md={6}>Proposed Structured Format</Col>
              </Row>
              <Row className="align-items-center">
                <Col md={6} className="border-right">
                  <div>
                    <span className="text-muted">Display Name:</span> {item.currentName || '—'}
                  </div>
                  <div>
                    <span className="text-muted">First Name:</span> {item.currentFirstName || '—'}
                  </div>
                  <div>
                    <span className="text-muted">Last Name:</span> {item.currentLastName || '—'}
                  </div>
                  <div>
                    <span className="text-muted">Suffix:</span> {item.currentSuffix || '—'}
                  </div>
                </Col>
                <Col md={6}>
                  <Row className="mb-1">
                    <Col sm={3}>
                      <Form.Label className="small mb-0 text-muted">Title</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="Dr."
                      />
                    </Col>
                    <Col sm={4}>
                      <Form.Label className="small mb-0 text-muted">First</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                      />
                    </Col>
                    <Col sm={5}>
                      <Form.Label className="small mb-0 text-muted">Last</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={6}>
                      <Form.Label className="small mb-0 text-muted">Middle</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={middleName}
                        onChange={e => setMiddleName(e.target.value)}
                      />
                    </Col>
                    <Col sm={6}>
                      <Form.Label className="small mb-0 text-muted">Suffix</Form.Label>
                      <Form.Control size="sm" type="text" value={suffix} onChange={e => setSuffix(e.target.value)} />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>

            <div className="d-flex justify-content-end align-items-center mt-2">
              <Button variant="outline-secondary" size="sm" className="mr-2" onClick={handleSkip} disabled={isApplying}>
                Skip
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleApply}
                disabled={isApplying || (!firstName.trim() && !lastName.trim())}
              >
                {isApplying ? 'Applying...' : 'Apply Correction'}
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  )
}
